import React, { useState, useEffect } from "react";

import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  X,
  Filter,
  Search,
  Calendar,
  TrendingUp,
  Loader2,
  User,
  Calculator,
  IndianRupee,
  Copy,
} from "lucide-react";
import { requestOrderService } from "@/services/api/request.order.service";
import { toast } from "react-hot-toast";

// Reusable Components
const StatusBadge = ({ status }) => {
  const config = {
    PENDING: {
      classes: "bg-amber-50 text-amber-700 border-amber-200 shadow-sm",
      icon: <Clock className="w-3 h-3" />,
    },
    APPROVED: {
      classes: "bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm",
      icon: <CheckCircle className="w-3 h-3" />,
    },
    REJECTED: {
      classes: "bg-red-50 text-red-700 border-red-200 shadow-sm",
      icon: <XCircle className="w-3 h-3" />,
    },
    CANCELLED: {
      classes: "bg-gray-50 text-gray-700 border-gray-200 shadow-sm",
      icon: <AlertCircle className="w-3 h-3" />,
    },
    PAYMENT_PENDING: {
      classes: "bg-orange-50 text-orange-700 border-orange-200 shadow-sm",
      icon: <Clock className="w-3 h-3" />,
    },
    AWAITING_ONLINE_PAYMENT: {
      classes: "bg-amber-50 text-amber-800 border-amber-200 shadow-sm",
      icon: <IndianRupee className="w-3 h-3" />,
    },
  };

  const { classes, icon } = config[status] || config.PENDING;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${classes}`}
    >
      {icon}
      {status.replace(/_/g, " ").charAt(0) + status.replace(/_/g, " ").slice(1).toLowerCase()}
    </span>
  );
};

const RequestCard = ({ request, onViewDetails, onCancel, cancelLoading, onInitiatePayment, processingPaymentId }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const hasModifications =
    request.adminResponse?.priceModified ||
    request.adminResponse?.quantityModified;
  const finalQuantity = hasModifications
    ? request.adminResponse?.customQuantity ?? request.requestedQuantity
    : request.requestedQuantity;
  const finalPrice = hasModifications
    ? request.adminResponse?.customBuyPrice ?? request.pricePerShare
    : request.pricePerShare;
  const finalTotal = hasModifications
    ? request.adminResponse?.newTotalAmount ?? request.totalAmount
    : request.totalAmount;

  return (
    <div className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200 hover:shadow-lg group">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg mb-1 group-hover:text-blue-600 transition-colors">
              {request.product?.name ||
                request.productId?.basicData?.legalName ||
                "Unknown Product"}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Portfolio:{" "}
              {request.portfolio?.name ||
                request.portfolioId?.portfolioName ||
                "Unknown Portfolio"}
            </p>
          </div>
          <StatusBadge status={request.status} />
        </div>

        {/* Investment Details */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
              Quantity
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {finalQuantity} units
              {hasModifications &&
                finalQuantity !== request.requestedQuantity && (
                  <span className="ml-2 text-xs text-green-600">
                    ({finalQuantity > request.requestedQuantity ? "+" : ""}
                    {finalQuantity - request.requestedQuantity})
                  </span>
                )}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
              Total Amount
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {formatCurrency(finalTotal)}
              {hasModifications && finalTotal !== request.totalAmount && (
                <span className="ml-2 text-xs text-green-600">
                  ({finalTotal > request.totalAmount ? "+" : ""}
                  {formatCurrency(finalTotal - request.totalAmount)})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Payment Details for Pending Payment */}
        {request.status === "PAYMENT_PENDING" && request.adminResponse?.bankAccountNumber && (
          <div className="bg-orange-50/50 border border-orange-100 rounded-lg p-3 mb-3 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-orange-800 font-bold text-xs">
                <IndianRupee className="w-3.5 h-3.5" />
                <span>Action Required</span>
              </div>
              <div className="px-1.5 py-0.5 bg-orange-100/80 text-orange-700 text-[8px] font-bold rounded uppercase tracking-wider border border-orange-200">
                Offline
              </div>
            </div>

            <p className="text-[11px] text-orange-700 mb-2">
              Transfer <span className="font-bold">{formatCurrency(finalTotal)}</span> to:
            </p>

            <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden divide-y divide-gray-100">
              {/* Row 1: Account Holder & Bank Name */}
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="px-3 py-2 bg-orange-50/30">
                  <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest italic">Account Holder</div>
                  <div className="text-xs font-bold text-gray-800 truncate leading-tight">
                    {request.adminResponse.accountHolderName || "N/A"}
                  </div>
                </div>
                <div className="px-3 py-2 bg-orange-50/30">
                  <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest italic">Bank Name</div>
                  <div className="text-xs font-bold text-gray-800 truncate leading-tight">
                    {request.adminResponse.bankName || "N/A"}
                  </div>
                </div>
              </div>

              {/* Row 2: Account Number & IFSC Code */}
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className="px-3 py-2.5 flex items-center justify-between group transition-colors hover:bg-gray-50/50">
                  <div className="overflow-hidden">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest italic">Account Number</div>
                    <div className="text-sm font-mono font-bold text-gray-900 tracking-tighter truncate">
                      {request.adminResponse.bankAccountNumber}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(request.adminResponse.bankAccountNumber);
                      toast.success("Account Number copied!");
                    }}
                    className="p-1.5 hover:bg-orange-100 rounded-lg text-gray-400 hover:text-orange-600 transition-all ml-1 flex-shrink-0"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="px-3 py-2.5 flex items-center justify-between group transition-colors hover:bg-gray-50/50">
                  <div className="overflow-hidden">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest italic">IFSC Code</div>
                    <div className="text-sm font-mono font-bold text-blue-900 uppercase tracking-wider truncate">
                      {request.adminResponse.ifscCode || "N/A"}
                    </div>
                  </div>
                  {request.adminResponse.ifscCode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(request.adminResponse.ifscCode);
                        toast.success("IFSC Code copied!");
                      }}
                      className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600 transition-all ml-1 flex-shrink-0"
                      title="Copy"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2 flex items-center gap-1.5 text-[9px] text-orange-600 font-medium italic opacity-80">
              <AlertCircle className="w-2 h-2" />
              <span>Investment processed after verification</span>
            </div>
          </div>
        )}

        {/* Action card for awaiting online payment */}
        {request.status === "AWAITING_ONLINE_PAYMENT" && (
          <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-3 mb-3 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-amber-800 font-bold text-xs">
                <IndianRupee className="w-3.5 h-3.5" />
                <span>Action Required</span>
              </div>
              <div className="px-1.5 py-0.5 bg-amber-100/80 text-amber-700 text-[8px] font-bold rounded uppercase tracking-wider border border-amber-200">
                Online Payment
              </div>
            </div>

            <p className="text-[11px] text-amber-800 mb-2">
              Complete your payment of <span className="font-bold">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(request.adminResponse?.newTotalAmount || request.totalAmount)}</span> using Razorpay.
            </p>

            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInitiatePayment(request._id);
                }}
                disabled={processingPaymentId === request._id}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {processingPaymentId === request._id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Pay with Online"
                )}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            {formatDate(request.createdAt)}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onViewDetails(request._id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>

            {request.status === "PENDING" && (
              <button
                onClick={() => onCancel(request._id)}
                disabled={cancelLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

const EmptyState = ({ selectedStatus }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
    <div className="max-w-sm mx-auto">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {selectedStatus
          ? `No ${selectedStatus.toLowerCase()} requests`
          : "No approval requests"}
      </h3>
      <p className="text-gray-600">
        {selectedStatus
          ? `You don't have any ${selectedStatus.toLowerCase()} approval requests yet.`
          : "You haven't made any approval requests yet. Start investing to see your requests here."}
      </p>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="flex justify-between items-start mb-4">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-48"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
            <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="h-16 bg-gray-100 rounded-lg"></div>
            <div className="h-16 bg-gray-100 rounded-lg"></div>
          </div>
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <div className="h-3 bg-gray-200 rounded w-24"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-gray-200 rounded w-24"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <XCircle className="w-8 h-8 text-red-500" />
    </div>
    <h3 className="text-lg font-semibold text-red-900 mb-2">
      Unable to load requests
    </h3>
    <p className="text-red-700 mb-4">{error}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
    >
      Try Again
    </button>
  </div>
);

const RequestDetailsModal = ({
  request,
  isOpen,
  onClose,
  onCancel,
  cancelLoading,
}) => {
  if (!isOpen || !request) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to check if there are modifications
  const hasModifications = request.adminResponse?.hasModifications || false;
  const finalValues = request.adminResponse?.finalValues || {};

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Request Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status & Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-2">
                Status
              </label>
              <StatusBadge status={request.status} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-2">
                Priority
              </label>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${request.priority === "HIGH"
                  ? "bg-red-100 text-red-800"
                  : request.priority === "MEDIUM"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
                  }`}
              >
                {request.priority || "MEDIUM"}
              </span>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-2">
                Request Type
              </label>
              <div className="text-sm text-gray-900 font-medium">
                {request.requestType || "N/A"}
              </div>
            </div>
          </div>

          {/* Investment Details */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Investment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">
                  Product
                </label>
                <div className="text-sm text-gray-900 font-medium">
                  {request.product?.name || "N/A"}
                </div>
                {request.product?.category && (
                  <div className="text-xs text-gray-500 mt-1">
                    {request.product.category}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">
                  Portfolio
                </label>
                <div className="text-sm text-gray-900">
                  {request.portfolio?.name || "N/A"}
                </div>
                {request.portfolio?.type && (
                  <div className="text-xs text-gray-500 mt-1">
                    {request.portfolio.type}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Price & Quantity Details */}
          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Price & Quantity Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Original Request */}
              <div>
                <h4 className="text-sm font-medium text-blue-700 mb-3">
                  Original Request
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 block">
                      Quantity
                    </label>
                    <div className="text-sm font-medium text-gray-900">
                      {request.requestedQuantity} units
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block">
                      Price per Share
                    </label>
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(request.pricePerShare)}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block">
                      Total Amount
                    </label>
                    <div className="text-lg font-bold text-gray-900">
                      {formatCurrency(request.totalAmount)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Final/Approved Values */}
              {(hasModifications || request.status === "APPROVED") && (
                <div>
                  <h4 className="text-sm font-medium text-green-700 mb-3">
                    {hasModifications ? "Modified Values" : "Approved Values"}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 block">
                        Quantity
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {finalValues.quantity || request.requestedQuantity}{" "}
                        units
                        {hasModifications &&
                          finalValues.quantity !==
                          request.requestedQuantity && (
                            <span className="ml-2 text-xs text-green-600">
                              (
                              {finalValues.quantity > request.requestedQuantity
                                ? "+"
                                : ""}
                              {finalValues.quantity - request.requestedQuantity}
                              )
                            </span>
                          )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block">
                        Price per Share
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(
                          finalValues.price || request.pricePerShare
                        )}
                        {hasModifications &&
                          finalValues.price !== request.pricePerShare && (
                            <span className="ml-2 text-xs text-green-600">
                              (
                              {finalValues.price > request.pricePerShare
                                ? "+"
                                : ""}
                              {formatCurrency(
                                finalValues.price - request.pricePerShare
                              )}
                              )
                            </span>
                          )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block">
                        Total Amount
                      </label>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(
                          finalValues.totalAmount || request.totalAmount
                        )}
                        {hasModifications &&
                          finalValues.totalAmount !== request.totalAmount && (
                            <span className="ml-2 text-sm text-green-600">
                              (
                              {finalValues.totalAmount > request.totalAmount
                                ? "+"
                                : ""}
                              {formatCurrency(
                                finalValues.totalAmount - request.totalAmount
                              )}
                              )
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Changes Summary */}
              {hasModifications && (
                <div>
                  <h4 className="text-sm font-medium text-orange-700 mb-3">
                    Changes Summary
                  </h4>
                  <div className="space-y-2">
                    {request.adminResponse?.quantityModified && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-xs text-gray-700">
                          Quantity Modified
                        </span>
                      </div>
                    )}
                    {request.adminResponse?.priceModified && (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-xs text-gray-700">
                          Price Modified
                        </span>
                      </div>
                    )}
                    {request.adminResponse?.changes?.totalAmountChange !==
                      0 && (
                        <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                          <div className="text-xs text-orange-700">
                            Net Change:{" "}
                            {formatCurrency(
                              Math.abs(
                                request.adminResponse.changes.totalAmountChange
                              )
                            )}
                            <span
                              className={
                                request.adminResponse.changes.totalAmountChange >
                                  0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              (
                              {request.adminResponse.changes.totalAmountChange > 0
                                ? "Increase"
                                : "Decrease"}
                              )
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Details */}
          {request.user && (
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                User Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">
                    Name
                  </label>
                  <div className="text-sm text-gray-900 font-medium">
                    {request.user.name}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">
                    Email
                  </label>
                  <div className="text-sm text-gray-900">
                    {request.user.email}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">
                    Mobile
                  </label>
                  <div className="text-sm text-gray-900">
                    {request.user.mobileNo}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500 block mb-2">
                Requested On
              </label>
              <div className="text-sm text-gray-900">
                {formatDate(request.createdAt)}
              </div>
            </div>
            {request.updatedAt !== request.createdAt && (
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-2">
                  Last Updated
                </label>
                <div className="text-sm text-gray-900">
                  {formatDate(request.updatedAt)}
                </div>
              </div>
            )}
          </div>

          {/* Admin Response & Offline Payment Info */}
          {request.adminResponse && (
            <div className={`rounded-xl p-6 border ${request.status === "PAYMENT_PENDING"
              ? "bg-orange-50 border-orange-100"
              : "bg-blue-50 border-blue-100"
              }`}>
              <h3 className={`font-semibold mb-4 ${request.status === "PAYMENT_PENDING" ? "text-orange-900" : "text-blue-900"
                }`}>
                {request.status === "PAYMENT_PENDING" ? "Payment Instructions" : "Admin Response"}
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className={`text-sm font-medium block ${request.status === "PAYMENT_PENDING" ? "text-orange-700" : "text-blue-700"
                    }`}>
                    Status
                  </label>
                  <StatusBadge status={request.status} />
                </div>

                {request.status === "PAYMENT_PENDING" && request.adminResponse.bankAccountNumber && (
                  <div className="space-y-3 py-1">
                    <div className="bg-white rounded-lg border border-orange-200 shadow overflow-hidden ring-1 ring-orange-50">
                      <div className="bg-orange-600 px-3 py-1.5 flex justify-between items-center">
                        <span className="text-[9px] font-bold text-white uppercase tracking-widest">Bank Details</span>
                        <IndianRupee className="w-3.5 h-3.5 text-orange-100" />
                      </div>

                      <div className="p-4 space-y-4">
                        <div className="p-4 space-y-4">
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">Amount Payable</label>
                            <div className="text-xl font-black text-gray-900">
                              {formatCurrency(finalValues.totalAmount || request.totalAmount)}
                            </div>
                          </div>

                          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-100 shadow-sm border-l-4 border-l-orange-500">
                            {/* Row 1: Holder & Bank */}
                            <div className="grid grid-cols-2 divide-x divide-gray-100">
                              <div className="p-3 bg-gray-50/50">
                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block">Account Holder</label>
                                <div className="text-sm font-bold text-gray-900 truncate">{request.adminResponse.accountHolderName || "N/A"}</div>
                              </div>
                              <div className="p-3 bg-gray-50/50">
                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block">Bank Name</label>
                                <div className="text-sm font-bold text-gray-900 truncate">{request.adminResponse.bankName || "N/A"}</div>
                              </div>
                            </div>

                            {/* Row 2: Number & IFSC */}
                            <div className="grid grid-cols-2 divide-x divide-gray-100">
                              <div className="p-3 flex items-center justify-between group hover:bg-gray-50 transition-colors">
                                <div className="overflow-hidden">
                                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block">Account Number</label>
                                  <div className="text-base font-mono font-bold text-blue-900 truncate tracking-tighter">
                                    {request.adminResponse.bankAccountNumber}
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(request.adminResponse.bankAccountNumber);
                                    toast.success("Account Number copied!");
                                  }}
                                  className="p-1.5 hover:bg-orange-50 rounded-lg text-gray-400 hover:text-orange-600 transition-all flex-shrink-0"
                                >
                                  <Copy className="w-5 h-5" />
                                </button>
                              </div>

                              <div className="p-3 flex items-center justify-between group hover:bg-gray-50 transition-colors">
                                <div className="overflow-hidden">
                                  <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block">IFSC Code</label>
                                  <div className="text-base font-mono font-bold text-gray-900 truncate uppercase tracking-wider">
                                    {request.adminResponse.ifscCode || "N/A"}
                                  </div>
                                </div>
                                {request.adminResponse.ifscCode && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(request.adminResponse.ifscCode);
                                      toast.success("IFSC Code copied!");
                                    }}
                                    className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600 transition-all flex-shrink-0"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-orange-50 px-4 py-2 border-t border-orange-100 flex items-start gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-orange-600 mt-0.5" />
                        <p className="text-[10px] text-orange-800 leading-relaxed">
                          Please transfer the exact amount and wait for verification.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {request.adminResponse.notes && (
                  <div>
                    <label className={`text-sm font-medium block mb-1 ${request.status === "PAYMENT_PENDING" ? "text-orange-700" : "text-blue-700"
                      }`}>
                      Admin Notes
                    </label>
                    <div className="text-sm text-gray-900 bg-white p-3 rounded border">
                      {request.adminResponse.notes}
                    </div>
                  </div>
                )}
                {request.adminResponse.reason && (
                  <div>
                    <label className={`text-sm font-medium block mb-1 ${request.status === "PAYMENT_PENDING" ? "text-orange-700" : "text-blue-700"
                      }`}>
                      Reason
                    </label>
                    <div className="text-sm text-gray-900 bg-white p-3 rounded border">
                      {request.adminResponse.reason}
                    </div>
                  </div>
                )}
                {request.adminResponse.decidedAt && (
                  <div className="flex justify-between">
                    <label className={`text-sm font-medium ${request.status === "PAYMENT_PENDING" ? "text-orange-700" : "text-blue-700"
                      }`}>
                      Decision Date
                    </label>
                    <div className="text-sm text-gray-900">
                      {formatDate(request.adminResponse.decidedAt)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Request Reason */}
          {request.requestReason && (
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-100">
              <h3 className="font-semibold text-yellow-900 mb-3">
                Request Reason
              </h3>
              <div className="text-sm text-yellow-900">
                {request.requestReason}
              </div>
            </div>
          )}

          {/* Actions */}
          {request.status === "PENDING" && (
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={() => onCancel(request._id)}
                disabled={cancelLoading}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
                Cancel Request
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const UserApprovalRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const statusOptions = [
    { value: "", label: "All Requests" },
    { value: "PENDING", label: "Pending" },
    { value: "APPROVED", label: "Approved" },
    { value: "ACTION_REQUIRED", label: "Payment Pending" },
    { value: "REJECTED", label: "Rejected" },
    { value: "CANCELLED", label: "Cancelled" },
  ];

  // Fetch requests
  const fetchRequests = async (page = 1, status = "") => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page,
        limit: 10,
        ...(status && { status }),
      };

      const response = await requestOrderService.getApprovalRequests(params);

      if (response.success) {
        setRequests(response.data || []);
        setPagination(response.pagination);
      } else {
        setError(response.error || "Failed to fetch requests");
      }
    } catch (err) {
      setError(err?.error || "Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchRequests(1, selectedStatus);
    setCurrentPage(1);
  }, [selectedStatus]);

  // Handle status filter change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchRequests(page, selectedStatus);
  };

  // View request details
  const viewRequestDetails = async (requestId) => {
    try {
      const response = await requestOrderService.getApprovalRequestDetails(
        requestId
      );
      if (response.success) {
        setSelectedRequest(response.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error("Failed to fetch request details:", err);
    }
  };

  // Cancel request
  const cancelRequest = async (requestId) => {
    try {
      setCancelLoading(true);
      const response = await requestOrderService.cancelApprovalRequest(
        requestId
      );

      if (response.success) {
        // Refresh the list
        fetchRequests(currentPage, selectedStatus);
        setShowModal(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error("Failed to cancel request:", err);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRetry = () => {
    fetchRequests(currentPage, selectedStatus);
  };

  const [processingPayment, setProcessingPayment] = useState(null);

  // Dynamically load Razorpay script
  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
      document.body.appendChild(script);
    });

  // Handle initiate online payment
  const handleInitiatePayment = async (requestId) => {
    if (!requestId) return;
    try {
      setProcessingPayment(requestId);
      await loadRazorpayScript();
      const res = await requestOrderService.initiateOnlinePayment(requestId);
      if (!res || !res.razorpayOrderId) {
        toast.error(res?.error || 'Failed to initiate payment');
        setProcessingPayment(null);
        return;
      }

      const options = {
        key: res.key,
        amount: res.amount,
        currency: res.currency,
        order_id: res.razorpayOrderId,
        name: import.meta.env.VITE_APP_NAME || 'Investhive',
        description: 'Investment Payment',
        handler: async function (response) {
          try {
            toast.loading('Verifying payment...', { id: 'payment-verify' });
            // Verify payment on backend
            const verifyRes = await requestOrderService.verifyOnlineInvestmentPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes && verifyRes.success) {
              toast.success('Payment Successful! Your portfolio is being updated.', { id: 'payment-verify' });
              // Refresh list
              fetchRequests(currentPage, selectedStatus);
            } else {
              toast.error(verifyRes?.error || 'Payment verification failed', { id: 'payment-verify' });
            }
          } catch (err) {
            console.error('Verification error', err);
            toast.error(err?.message || 'Payment verification failed', { id: 'payment-verify' });
          } finally {
            setProcessingPayment(null);
          }
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(null);
          }
        },
        prefill: {
          name: '', // Optional: can be filled if user data is available
          email: '',
          contact: ''
        },
        theme: {
          color: "#D97706" // Amber-600 to match the UI
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Payment init error', err);
      toast.error(err?.message || 'Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            My Approval Requests
          </h1>
          <p className="text-gray-600">
            Track your investment approval requests and their status
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">
                Filter by Status:
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleStatusChange(value)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${selectedStatus === value
                    ? "bg-blue-100 text-blue-700 border-2 border-blue-200 shadow-sm"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={handleRetry} />
        ) : requests.length === 0 ? (
          <EmptyState selectedStatus={selectedStatus} />
        ) : (
          <>
            {/* Requests List */}
            <div className="space-y-4 mb-8">
              {requests.map((request) => (
                <RequestCard
                  key={request._id}
                  request={request}
                  onViewDetails={viewRequestDetails}
                  onCancel={cancelRequest}
                  cancelLoading={cancelLoading}
                  onInitiatePayment={handleInitiatePayment}
                  processingPaymentId={processingPayment}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.total > 1 && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-700 order-2 sm:order-1">
                    Showing {(currentPage - 1) * 10 + 1}-
                    {Math.min(currentPage * 10, pagination.totalRecords)} of{" "}
                    {pagination.totalRecords} results
                  </div>
                  <div className="flex gap-2 order-1 sm:order-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg font-medium border border-blue-200">
                      {currentPage} of {pagination.total}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pagination.total}
                      className="px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Details Modal */}
      <RequestDetailsModal
        request={selectedRequest}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCancel={cancelRequest}
        cancelLoading={cancelLoading}
      />
    </div>
  );
};

export default UserApprovalRequests;
